import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, Mail, Send, Inbox, RefreshCw, Trash2, Eye, Reply, 
  Clock, User, FileText, AlertCircle, CheckCircle, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_ADDRESS = 'contato@toindoali.com.br';

const AdminEmails = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('compose'); // compose, sent
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [composeData, setComposeData] = useState({
    to: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadSentEmails();
  }, [navigate]);

  const loadSentEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/emails/sent`);
      setSentEmails(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar emails:', error);
      setSentEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!composeData.to || !composeData.subject || !composeData.body) {
      setMessage({ type: 'error', text: 'Preencha todos os campos' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      await axios.post(`${BACKEND_URL}/api/emails/send`, {
        to: composeData.to,
        subject: composeData.subject,
        body: composeData.body,
        from: EMAIL_ADDRESS
      });

      setMessage({ type: 'success', text: 'E-mail enviado com sucesso!' });
      setComposeData({ to: '', subject: '', body: '' });
      setShowComposeModal(false);
      loadSentEmails();
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao enviar e-mail' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6" />
                <div>
                  <h1 className="text-xl font-bold">Central de E-mails</h1>
                  <p className="text-sm text-gray-300">{EMAIL_ADDRESS}</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => setShowComposeModal(true)}
              className="bg-white text-[#003366] hover:bg-gray-100"
            >
              <Send className="w-4 h-4 mr-2" />
              Novo E-mail
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Info Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-6 h-6 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Gerenciamento de E-mails</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Aqui você pode enviar e-mails usando o endereço <strong>{EMAIL_ADDRESS}</strong>.
                  Para receber e-mails, acesse sua caixa de entrada diretamente no provedor de e-mail.
                </p>
                <a 
                  href="https://resend.com/emails" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Acessar painel Resend
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button 
            variant={activeTab === 'compose' ? 'default' : 'outline'}
            onClick={() => setActiveTab('compose')}
            className={activeTab === 'compose' ? 'bg-[#003366]' : ''}
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar E-mail
          </Button>
          <Button 
            variant={activeTab === 'sent' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sent')}
            className={activeTab === 'sent' ? 'bg-[#003366]' : ''}
          >
            <FileText className="w-4 h-4 mr-2" />
            E-mails Enviados
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.open('https://mail.zoho.com/zm/#mail/folder/inbox', '_blank')}
            className="bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
          >
            <Inbox className="w-4 h-4 mr-2" />
            E-mails Recebidos (Zoho)
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'compose' && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Send className="w-5 h-5 text-[#003366]" />
                Compor E-mail
              </h2>

              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label>De</Label>
                  <Input value={EMAIL_ADDRESS} disabled className="mt-1 bg-gray-100" />
                </div>

                <div>
                  <Label>Para *</Label>
                  <Input 
                    type="email"
                    placeholder="email@exemplo.com"
                    value={composeData.to}
                    onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Assunto *</Label>
                  <Input 
                    placeholder="Assunto do e-mail"
                    value={composeData.subject}
                    onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Mensagem *</Label>
                  <Textarea 
                    placeholder="Escreva sua mensagem aqui..."
                    value={composeData.body}
                    onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                    className="mt-1 min-h-[200px]"
                  />
                </div>

                <Button 
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="bg-[#003366] hover:bg-[#002244]"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar E-mail
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'sent' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#003366]" />
                  Histórico de Envios
                </h2>
                <Button variant="outline" size="sm" onClick={loadSentEmails} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">Carregando...</div>
              ) : sentEmails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Nenhum e-mail enviado ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sentEmails.map((email) => (
                    <div 
                      key={email.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedEmail(email)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{email.to}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mt-1">{email.subject}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{email.body?.substring(0, 100)}...</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="text-xs text-gray-500">{formatDate(email.sent_at)}</div>
                          <div className={`text-xs mt-1 ${
                            email.status === 'sent' ? 'text-green-600' : 
                            email.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                          }`}>
                            {email.status === 'sent' ? '✓ Enviado' : 
                             email.status === 'failed' ? '✗ Falhou' : '⏳ Pendente'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Modal de Visualização */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do E-mail</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">Para</Label>
                  <p className="font-medium">{selectedEmail.to}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Enviado em</Label>
                  <p className="font-medium">{formatDate(selectedEmail.sent_at)}</p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Assunto</Label>
                <p className="font-medium">{selectedEmail.subject}</p>
              </div>
              <div>
                <Label className="text-gray-500">Mensagem</Label>
                <div className="mt-1 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                  {selectedEmail.body}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmails;
